// session.js — the single owner of the browser-side session.
//
// Before this existed, five files each reached into localStorage directly and
// four of them ended the session with a bare `localStorage.clear()`. That had
// two problems:
//
//   1. `clear()` removes *everything*, including the user's `theme` choice, so
//      every sign-out, idle timeout and 401 silently reset dark mode to light.
//   2. Ending a session is about to stop being "delete a key" — under Cognito
//      SSO it becomes "delete tokens, then redirect to the hosted /logout with
//      the right logout_uri". That belongs in one place, not four.
//
// Nothing here knows about React or axios, so it is importable from components,
// interceptors and tests alike without a provider or a mock.
//
// TARGET PATH: src/shared/auth/session.js

import authConfig, { isConfigured } from '../config/authConfig';

// ============================================
// What survives a sign-out
// ============================================
// Deliberately an allowlist of what to KEEP, not a list of what to remove.
//
// The old `clear()` was crude but it failed safe: it could never forget to
// remove something. A hand-listed AUTH_KEYS array would fail open — the day
// someone adds a new auth key and forgets to list it, it survives logout and
// leaks into the next session on a shared machine. Inverting it keeps the
// fail-safe property: anything unrecognised is treated as session state and
// cleared, and only these explicitly non-identity preferences are spared.
const KEEP_ON_LOGOUT = ['theme'];

const TOKEN = 'token';
const USERNAME = 'username';
const TOKEN_EXPIRY = 'tokenExpiry';

// ============================================
// The SSO session, read without React
// ============================================
// The axios interceptors need a token and are not components, so they cannot use
// react-oidc-context's hook. oidc-client-ts persists the signed-in user under a
// well-known sessionStorage key, so it is read directly here. That keeps this
// module free of React and importable from interceptors and tests alike.
const ssoStorageKey = () => `oidc.user:${authConfig.issuer}:${authConfig.clientId}`;

/**
 * The **ID** token from a Cognito session, or null when there is not one.
 *
 * Deliberately the ID token and not the access token. The backend rejects
 * anything where `token_use != "id"`, because Cognito access tokens carry no
 * `email` claim and accepting them would leave the API without an identity.
 * Sending the access token produces a 401 that looks exactly like a broken
 * login, which is a bad afternoon.
 */
export const getSsoIdToken = () => {
  if (!isConfigured()) return null;
  try {
    const raw = window.sessionStorage.getItem(ssoStorageKey());
    if (!raw) return null;
    return JSON.parse(raw).id_token || null;
  } catch {
    // Malformed storage should degrade to "not signed in via SSO" and let the
    // legacy token answer, rather than throwing inside an axios interceptor
    // where it would surface as an unrelated request failure.
    return null;
  }
};

/**
 * The claims on the ID token, or null when there is no SSO session.
 *
 * This DECODES; it does not verify. That is deliberate and is what the handoff
 * asks for: "Read claims for display only, treat the token as opaque otherwise.
 * Verification is the backend's job and it already does it properly." So there
 * is no signature check and no JWKS fetch. The worst a tampered token achieves
 * here is a wrong name on screen — the backend rejects it either way.
 *
 * The payload is base64url, so `-` and `_` are swapped back and the padding
 * restored before atob, which only accepts standard base64.
 */
const ssoClaims = () => {
  const token = getSsoIdToken();
  if (!token) return null;
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = part.padEnd(Math.ceil(part.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

// ============================================
// Reads
// ============================================
/**
 * The bearer token for API calls: the Cognito ID token when there is an SSO
 * session, otherwise the legacy JWT from POST /v1.0/auth/login.
 *
 * Both paths are live on purpose. The backend accepts both for the whole
 * rollout, and sitops and prodops have no Cognito pool yet — so a user who
 * signed in with a password must keep working while devops moves to Microsoft.
 * SSO wins when present because it is the direction of travel; when the legacy
 * endpoint is finally deleted, the fallback goes with it and this becomes a
 * one-liner again.
 */
export const getToken = () => getSsoIdToken() || localStorage.getItem(TOKEN);

/**
 * The name to show a person: "Good morning, Nakedi".
 *
 * Prefers `given_name`, falls back to `email`, then to the legacy stored
 * username so password sessions are unaffected.
 */
export const getDisplayName = () => {
  const claims = ssoClaims();
  return claims?.given_name || claims?.email || localStorage.getItem(USERNAME);
};

/**
 * The identity to write onto a record — `changed_by`, `assigned_by` and friends.
 *
 * Deliberately `email` and NOT `given_name`, even though the display name
 * prefers the latter. A first name is useless in an audit trail: two people
 * called Nakedi are indistinguishable in the record forever. Email also matches
 * what the backend uses for identity — CognitoAuthFilter reads the `email` claim
 * — so a row's author matches the principal the API authenticated.
 *
 * Split from getDisplayName rather than left as one getUsername() because the
 * two callers want different things, and a single function meant whichever
 * choice was made would be wrong for half of them.
 */
export const getUserIdentifier = () => {
  const claims = ssoClaims();
  return claims?.email || localStorage.getItem(USERNAME);
};

export const isAuthenticated = () => Boolean(getToken());

/**
 * True only when a usable expiry exists and has passed.
 *
 * The `Boolean(expiry)` guard matters: sessions created before `tokenExpiry`
 * shipped have no expiry stored, and treating a missing value as "expired"
 * would sign those users out the moment a deploy landed. A non-numeric value
 * becomes NaN, which is also falsy, so garbage in storage is ignored rather
 * than locking anyone out.
 */
export const isExpired = () => {
  const expiry = Number(localStorage.getItem(TOKEN_EXPIRY));
  return Boolean(expiry) && Date.now() >= expiry;
};

// ============================================
// Writes
// ============================================
/**
 * Records a successful sign-in.
 *
 * `expiresIn` is a duration in milliseconds as returned by
 * POST /v1.0/auth/login; it is stored as an absolute timestamp so expiry can be
 * checked without knowing when the session started.
 */
export const startSession = ({ token, username, expiresIn }) => {
  localStorage.setItem(TOKEN, token);
  localStorage.setItem(USERNAME, username);
  localStorage.setItem(TOKEN_EXPIRY, Date.now() + expiresIn);
};

/**
 * Ends the session, keeping only the KEEP_ON_LOGOUT preferences.
 *
 * Clears BOTH stores. The legacy JWT lives in localStorage; the SSO session —
 * including the refresh token, which is 30 days of API access — lives in
 * sessionStorage. Clearing only one would leave a user who believes they signed
 * out with a usable token still sitting in the tab.
 *
 * Keys are collected before removing any, because mutating a storage area while
 * iterating its live index skips entries.
 */
export const endSession = () => {
  [localStorage, sessionStorage].forEach((store) => {
    Object.keys(store)
      .filter((key) => !KEEP_ON_LOGOUT.includes(key))
      .forEach((key) => store.removeItem(key));
  });
};
